import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton as MuiIconButton,
  Stack,
} from '@mui/material';
import {
  SlideshowOutlined,
  NavigateNext,
  NavigateBefore,
  FirstPage,
  LastPage,
  Input,
  Close,
} from '@mui/icons-material';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useControlSlideMutation } from '@/state/mutations/player/use-control-slide';
import { GET_PDF_PAGE_STATUS_QUERY_KEY } from '@/state/queries/player/use-get-pdf-page-status';
import { GET_SEQUENCE_INDEX_STATUS_QUERY_KEY } from '@/state/queries/player/use-get-sequence-index-status';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { useLocalMediaPreviewSlideStore } from '@/state/stores/use-local-media-preview-slide-store';
import { useLiveSequenceIndexStore } from '@/state/stores/use-live-sequence-index-store';
import { useFlowControlLiveState } from '@/components/flows/detail/flow-control-live-state';
import { useLiveContextStateWithLivePlayState } from '@/hooks/use-live-play-state';
import { isMediaAgentBackupSlidesActive } from '@/helpers/media-agent-recovery.helper';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import {
  resolveFirstFrameForSlide,
  resolvePresentationStepPosition,
} from '@/helpers/presentation-step-position.helper';

export type SlideControlMode = 'live' | 'local' | 'simulation';

interface SlideControlButtonProps {
  mediaId: string;
  entityType: string;
  entityId: string;
  disabled?: boolean;
  currentSlideIndex?: number;
  totalSlides?: number;
  speakerNotes?: string;
  /** When 'pdf' or 'imageSequence': hide speaker notes; Last/Go to disabled when totalSlides unknown */
  mediaType?: 'presentation' | 'pdf' | 'imageSequence';
  /** live: backend control-slide API; local: asset preview only; simulation: flow-control simulation store */
  controlMode?: SlideControlMode;
  /** Presentation step mode: slide number per sequence frame (indexes count animation steps). */
  frameSlideNumbers?: number[];
}

export const SlideControlButton = ({
  mediaId,
  entityType,
  entityId,
  disabled,
  currentSlideIndex,
  totalSlides,
  speakerNotes,
  mediaType = 'presentation',
  controlMode = 'live',
  frameSlideNumbers,
}: SlideControlButtonProps) => {
  const isPdf = mediaType === 'pdf';
  const hideSpeakerNotes = mediaType === 'pdf' || mediaType === 'imageSequence';
  const itemId = `media:${mediaId}`;
  const t = useTranslations();
  const notification = useNotifications();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [gotoDialogOpen, setGotoDialogOpen] = useState(false);
  const [slideNumber, setSlideNumber] = useState<string>('');
  const { mutateAsync: controlSlide, isPending } = useControlSlideMutation();
  const { liveContextState } = useFlowControlLiveState();
  const liveContextMerged = useLiveContextStateWithLivePlayState(
    entityType && entityId ? `${entityType}:${entityId}` : undefined,
    liveContextState,
  );
  const backupSlidesActive = isMediaAgentBackupSlidesActive(liveContextMerged?.playState);

  const simulationSlideIndex = useFlowControlSimulationCueStore((s) => s.slideIndexByItemId[itemId] ?? 1);
  const simulationTotalSlides = useFlowControlSimulationCueStore((s) => s.totalPagesByItemId[itemId]);
  const stepSimulationSlide = useFlowControlSimulationCueStore((s) => s.stepSlide);
  const setSimulationSlideIndex = useFlowControlSimulationCueStore((s) => s.setSlideIndex);

  const localSlideIndex = useLocalMediaPreviewSlideStore((s) => s.indexByMediaId[mediaId] ?? 1);
  const localTotalSlides = useLocalMediaPreviewSlideStore((s) => s.totalPagesByMediaId[mediaId]);
  const stepLocalSlide = useLocalMediaPreviewSlideStore((s) => s.stepSlide);
  const setLocalSlideIndex = useLocalMediaPreviewSlideStore((s) => s.setSlideIndex);

  const resolvedCurrentSlideIndex =
    controlMode === 'simulation' ? simulationSlideIndex : controlMode === 'local' ? localSlideIndex : currentSlideIndex;
  const resolvedTotalSlides =
    controlMode === 'simulation'
      ? (simulationTotalSlides ?? totalSlides)
      : controlMode === 'local'
        ? (localTotalSlides ?? totalSlides)
        : totalSlides;

  // Only trust the slide map when it describes the same frame sequence the player reports.
  const stepFrameSlideNumbers =
    frameSlideNumbers?.length && frameSlideNumbers.length === resolvedTotalSlides ? frameSlideNumbers : undefined;
  const stepPosition = resolvePresentationStepPosition(resolvedCurrentSlideIndex, stepFrameSlideNumbers);

  const invalidateSlideIndexStatus = () => {
    queryClient.invalidateQueries({ queryKey: [GET_PDF_PAGE_STATUS_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [GET_SEQUENCE_INDEX_STATUS_QUERY_KEY] });
  };

  const applyLocalAction = (action: 'next' | 'prev' | 'first' | 'last' | 'goto', slideNumberValue?: number) => {
    const max = resolvedTotalSlides;
    switch (action) {
      case 'next':
        stepLocalSlide(mediaId, 1);
        return;
      case 'prev':
        stepLocalSlide(mediaId, -1);
        return;
      case 'first':
        setLocalSlideIndex(mediaId, 1);
        return;
      case 'last':
        if (max) {
          setLocalSlideIndex(mediaId, max);
        }
        return;
      case 'goto':
        if (slideNumberValue != null) {
          setLocalSlideIndex(mediaId, slideNumberValue);
        }
        return;
    }
  };

  const applySimulationAction = (action: 'next' | 'prev' | 'first' | 'last' | 'goto', slideNumberValue?: number) => {
    const max = resolvedTotalSlides;
    switch (action) {
      case 'next':
        stepSimulationSlide(itemId, 1);
        return;
      case 'prev':
        stepSimulationSlide(itemId, -1);
        return;
      case 'first':
        setSimulationSlideIndex(itemId, 1);
        return;
      case 'last':
        if (max) {
          setSimulationSlideIndex(itemId, max);
        }
        return;
      case 'goto':
        if (slideNumberValue != null) {
          setSimulationSlideIndex(itemId, slideNumberValue);
        }
        return;
    }
  };

  const handleOpen = () => {
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  const handleAction = async (action: 'next' | 'prev' | 'first' | 'last' | 'goto') => {
    if (action === 'goto') {
      setGotoDialogOpen(true);
      return;
    }

    if (controlMode === 'local') {
      applyLocalAction(action);
      return;
    }

    if (controlMode === 'simulation') {
      applySimulationAction(action);
      return;
    }

    const current = resolvedCurrentSlideIndex ?? 1;
    const max = resolvedTotalSlides;
    let target: number | undefined;
    switch (action) {
      case 'next':
        target = max != null && max > 0 ? Math.min(current + 1, max) : current + 1;
        break;
      case 'prev':
        target = Math.max(1, current - 1);
        break;
      case 'first':
        target = 1;
        break;
      case 'last':
        if (max == null || max < 1) {
          notification.notifyError(t('media.slideControl.error' as TranslationKey));
          return;
        }
        target = max;
        break;
      default:
        return;
    }

    const sendNativeStep =
      mediaType === 'presentation' && (action === 'next' || action === 'prev') && !backupSlidesActive;
    try {
      if (sendNativeStep) {
        useLiveSequenceIndexStore.getState().markNativePending(itemId);
        await controlSlide({
          mediaId,
          entityType,
          entityId,
          action,
        });
      } else {
        await controlSlide({
          mediaId,
          entityType,
          entityId,
          action: 'goto',
          slideNumber: target,
        });
      }
      invalidateSlideIndexStatus();
      notification.notifySuccess(t('media.slideControl.success' as TranslationKey));
    } catch (error) {
      if (sendNativeStep) {
        useLiveSequenceIndexStore.getState().clearNativePending(itemId);
      }
      console.error('[SlideControl] Error:', error);
      notification.notifyError(t('media.slideControl.error' as TranslationKey));
    }
  };

  const handleGotoSubmit = async () => {
    const requestedSlide = parseInt(slideNumber, 10);
    if (isNaN(requestedSlide) || requestedSlide < 1) {
      notification.notifyError(t('media.slideControl.invalidSlideNumber' as TranslationKey));
      return;
    }

    const slideTotal = stepFrameSlideNumbers ? Math.max(...stepFrameSlideNumbers) : resolvedTotalSlides;
    if (!hideSpeakerNotes && slideTotal !== undefined && requestedSlide > slideTotal) {
      notification.notifyError(
        t('media.slideControl.slideNumberExceedsTotal' as TranslationKey, { total: slideTotal }),
      );
      return;
    }

    // In step mode the sequence index counts animation steps; jump to the slide's first step.
    const num = resolveFirstFrameForSlide(requestedSlide, stepFrameSlideNumbers) ?? requestedSlide;

    if (controlMode === 'local') {
      applyLocalAction('goto', num);
      setGotoDialogOpen(false);
      setSlideNumber('');
      return;
    }

    if (controlMode === 'simulation') {
      applySimulationAction('goto', num);
      setGotoDialogOpen(false);
      setSlideNumber('');
      return;
    }

    try {
      await controlSlide({
        mediaId,
        entityType,
        entityId,
        action: 'goto',
        slideNumber: num,
      });
      invalidateSlideIndexStatus();
      notification.notifySuccess(t('media.slideControl.success' as TranslationKey));

      setGotoDialogOpen(false);
      setSlideNumber('');
    } catch (error) {
      console.error('[SlideControl] Goto error:', error);
      notification.notifyError(t('media.slideControl.error' as TranslationKey));
    }
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        disabled={disabled || isPending}
        size="medium"
        aria-label={t('media.slideControl.open' as TranslationKey)}
        sx={{ '& .MuiSvgIcon-root': { fontSize: '1.5rem' } }}
      >
        <SlideshowOutlined />
      </IconButton>

      <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>{t('media.slideControl.title' as TranslationKey)}</Box>
          <MuiIconButton onClick={handleClose} size="small" sx={{ ml: 'auto' }}>
            <Close />
          </MuiIconButton>
        </DialogTitle>
        <DialogContent>
          {/* Always show the box, with placeholder text if data not available */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            <Box sx={{ fontSize: '1rem', fontWeight: 'bold', mb: 2 }}>
              {stepPosition ? (
                t('media.slideControl.slideStepCounter' as TranslationKey, {
                  slide: stepPosition.slideNumber,
                  slides: stepPosition.slideTotal,
                  step: stepPosition.step,
                  steps: stepPosition.stepCount,
                })
              ) : resolvedCurrentSlideIndex !== undefined && resolvedTotalSlides !== undefined ? (
                <>
                  {t('media.slideControl.slideCounter' as TranslationKey, {
                    current: resolvedCurrentSlideIndex,
                    total: resolvedTotalSlides,
                  })}
                </>
              ) : hideSpeakerNotes ? (
                <Box sx={{ color: 'text.secondary' }}>
                  {resolvedCurrentSlideIndex !== undefined
                    ? t('media.slideControl.pageCounter' as TranslationKey, { current: resolvedCurrentSlideIndex })
                    : t('media.slideControl.waitingForData' as TranslationKey)}
                </Box>
              ) : (
                <Box sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  {t('media.slideControl.waitingForData' as TranslationKey)}
                </Box>
              )}
            </Box>

            {!hideSpeakerNotes && (
              <Box>
                <Box sx={{ fontSize: '0.875rem', fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>
                  {t('media.slideControl.speakerNotes' as TranslationKey)}
                </Box>
                <Box
                  sx={{
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    color: speakerNotes ? 'text.primary' : 'text.disabled',
                    fontStyle: speakerNotes ? 'normal' : 'italic',
                    maxHeight: '150px',
                    overflowY: 'auto',
                    p: 1,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  {speakerNotes || t('media.slideControl.noSpeakerNotes' as TranslationKey)}
                </Box>
              </Box>
            )}
          </Box>

          <Stack spacing={2}>
            <Button
              variant="outlined"
              startIcon={<FirstPage />}
              onClick={() => handleAction('first')}
              disabled={isPending}
              fullWidth
            >
              {isPdf
                ? t('media.slideControl.firstPage' as TranslationKey)
                : t('media.slideControl.firstSlide' as TranslationKey)}
            </Button>
            <Button
              variant="outlined"
              startIcon={<NavigateBefore />}
              onClick={() => handleAction('prev')}
              disabled={isPending}
              fullWidth
            >
              {t('media.slideControl.previous' as TranslationKey)}
            </Button>
            <Button
              variant="outlined"
              startIcon={<NavigateNext />}
              onClick={() => handleAction('next')}
              disabled={isPending}
              fullWidth
            >
              {t('media.slideControl.next' as TranslationKey)}
            </Button>
            <Button
              variant="outlined"
              startIcon={<LastPage />}
              onClick={() => handleAction('last')}
              disabled={isPending || (hideSpeakerNotes && resolvedTotalSlides === undefined)}
              fullWidth
            >
              {isPdf
                ? t('media.slideControl.lastPage' as TranslationKey)
                : t('media.slideControl.lastSlide' as TranslationKey)}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Input />}
              onClick={() => handleAction('goto')}
              disabled={isPending}
              fullWidth
            >
              {isPdf
                ? t('media.slideControl.goToPage' as TranslationKey)
                : t('media.slideControl.goToSlide' as TranslationKey)}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={gotoDialogOpen} onClose={() => setGotoDialogOpen(false)}>
        <DialogTitle>
          {isPdf
            ? t('media.slideControl.goToPageDialogTitle' as TranslationKey)
            : t('media.slideControl.goToSlideDialogTitle' as TranslationKey)}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={
              isPdf
                ? t('media.slideControl.pageNumber' as TranslationKey)
                : t('media.slideControl.slideNumber' as TranslationKey)
            }
            type="number"
            fullWidth
            value={slideNumber}
            onChange={(e) => {
              // Only allow empty string or positive integers
              const val = e.target.value;
              if (val === '' || (/^\d+$/.test(val) && Number(val) > 0)) {
                setSlideNumber(val);
              }
            }}
            onKeyDown={(e) => {
              // Handle Enter key to submit the form
              if (e.key === 'Enter') {
                e.preventDefault();
                handleGotoSubmit();
                return;
              }

              // Prevent entering 'e', 'E', '+', '-', and '.'
              if (
                ['e', 'E', '+', '-', '.'].includes(e.key) ||
                // Prevent space
                e.key === ' ' ||
                // Prevent any non-digit key except navigation/editing
                (!/^\d$/.test(e.key) &&
                  !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'].includes(e.key))
              ) {
                e.preventDefault();
              }
            }}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGotoDialogOpen(false)}>{t('common.cancel' as TranslationKey)}</Button>
          <Button onClick={handleGotoSubmit} variant="contained" disabled={isPending}>
            {t('media.slideControl.go' as TranslationKey)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
